import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart'
import EditIcon from '@mui/icons-material/Edit'
import EmailIcon from '@mui/icons-material/Email'
import PhoneIcon from '@mui/icons-material/Phone'
import { Box, Button, IconButton, LinearProgress, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { format } from 'date-fns'
import { cs, enUS } from 'date-fns/locale'

import { LessonCard } from '../../components/LessonCard'
import { formatLessonDate, useLessonGroups } from '../../components/LessonCard/utils'
import { toast } from '../../components/LsToast'
import { ScreenWrapper } from '../../components/ScreenWrapper'
import { CURRENCY } from '../../constants/currency'
import { Path } from '../../router/BaseRouter/enums'
import { getCustomer } from '../../service/customers.service'
import { getLessonsByCustomer } from '../../service/lessons.service'
import { BookingDto, CustomerDto, LessonDto, LessonStatus, LessonType } from '../../service/types'
import { useAppDispatch } from '../../store/hooks'
import { dayPlanActions } from '../../store/slices/dayPlanSlice'
import { drawerMarginSx } from '../../utils/drawerMargin'
import { CustomerDialog } from '../Customers/CustomerDialog'
import { CART_DRAWER_WIDTH, CustomerCart } from './CustomerCart'

type FilterMode = 'upcoming' | 'all'

export const CustomerDetail: FC = () => {
  const { customerId } = useParams<{ customerId: string }>()
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const [customer, setCustomer] = useState<CustomerDto | null>(null)
  const [lessons, setLessons] = useState<LessonDto[]>([])
  const [loading, setLoading] = useState(true)
  const [filterMode, setFilterMode] = useState<FilterMode>('upcoming')
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [cartItems, setCartItems] = useState<BookingDto[]>([])

  const dateLocale = i18n.language === 'cs' ? cs : enUS

  const loadData = useCallback(async () => {
    if (!customerId) return
    setLoading(true)
    try {
      const [customerRes, lessonsRes] = await Promise.all([getCustomer(customerId), getLessonsByCustomer(customerId)])
      setCustomer(customerRes.data)
      setLessons(lessonsRes.data)
    } catch {
      toast.error(t('general.notification.unexpectedError'))
    } finally {
      setLoading(false)
    }
  }, [customerId, t])

  useEffect(() => {
    void loadData()
  }, [loadData])

  // Clear cart items that are no longer valid after data reload
  useEffect(() => {
    const allBookingIds = new Set(lessons.flatMap((l) => l.bookings.map((b) => b.id)))
    setCartItems((prev) => prev.filter((item) => allBookingIds.has(item.id)))
  }, [lessons])

  const today = format(new Date(), 'yyyy-MM-dd')

  const filteredLessons = useMemo(() => {
    if (filterMode === 'upcoming') {
      return lessons.filter((l) => l.date >= today)
    }
    return lessons
  }, [lessons, filterMode, today])

  const { groupedByDate, sortedDates } = useLessonGroups(filteredLessons)

  const handleOpenInDayPlan = (date: string, lessonId: string) => {
    dispatch(dayPlanActions.setDate(date))
    navigate(`${Path.DAY_PLAN}/${lessonId}`)
  }

  const cartItemIds = useMemo(() => new Set(cartItems.map((b) => b.id)), [cartItems])

  const handleAddToCart = (booking: BookingDto) => {
    if (!cartItemIds.has(booking.id)) {
      setCartItems((prev) => [...prev, booking])
    }
  }

  const handleRemoveLessonFromCart = (lessonId: string) => {
    setCartItems((prev) => prev.filter((b) => b.lessonId !== lessonId))
  }

  const handlePaid = () => {
    setCartItems([])
    void loadData()
  }

  const handleCustomerSaved = () => {
    void loadData()
  }

  if (loading) {
    return <LinearProgress />
  }

  if (!customer) {
    return (
      <ScreenWrapper title="">
        <Typography sx={{ p: 3 }}>{t('customerDetail.noCustomerFound')}</Typography>
      </ScreenWrapper>
    )
  }

  return (
    <>
      {/* Main content */}
      <Box sx={{ p: 3, ...drawerMarginSx(cartItems.length > 0, CART_DRAWER_WIDTH) }}>
        {/* Customer header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            {customer.name}
          </Typography>
          <IconButton size="small" onClick={() => setEditDialogOpen(true)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
          {customer.phone && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PhoneIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {customer.phone}
              </Typography>
            </Box>
          )}
          {customer.email && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <EmailIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {customer.email}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Filter toggle */}
        <Box sx={{ mb: 2 }}>
          <ToggleButtonGroup
            value={filterMode}
            exclusive
            onChange={(_, value) => value && setFilterMode(value)}
            size="small"
          >
            <ToggleButton value="upcoming">{t('customers.lessons.upcoming')}</ToggleButton>
            <ToggleButton value="all">{t('customers.lessons.all')}</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Lesson list */}
        {filteredLessons.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 4 }}>
            {t('customers.lessons.noLessons')}
          </Typography>
        )}

        {sortedDates.map((date) => (
          <Box key={date} sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              {formatLessonDate(date, dateLocale)}
            </Typography>

            {groupedByDate[date].map((lesson) => (
              <LessonCard key={lesson.id} lesson={lesson} onOpenInDayPlan={handleOpenInDayPlan}>
                {/* Unpaid bookings — only for scheduled lessons */}
                {(() => {
                  if (lesson.status !== LessonStatus.Scheduled) return null
                  const unpaidBookings = lesson.bookings.filter((b) => !b.receiptId && b.customerId === customerId)
                  if (unpaidBookings.length === 0) return null

                  if (lesson.lessonType === LessonType.Private) {
                    const allInCart = unpaidBookings.every((b) => cartItemIds.has(b.id))
                    return (
                      <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                        {unpaidBookings.map((b) => (
                          <Typography key={b.id} variant="body2">
                            {b.skierName} ({b.skierAge}) — {Number(b.price).toFixed(2)} {CURRENCY}
                          </Typography>
                        ))}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                          <Button
                            size="small"
                            startIcon={<AddShoppingCartIcon />}
                            onClick={() => unpaidBookings.forEach((b) => handleAddToCart(b))}
                            disabled={allInCart}
                          >
                            {t('customerDetail.addToCart')}
                          </Button>
                        </Box>
                      </Box>
                    )
                  }

                  return unpaidBookings.map((booking) => (
                    <Box
                      key={booking.id}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: 1,
                        pt: 1,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="body2">
                        {booking.skierName} ({booking.skierAge}) — {Number(booking.price).toFixed(2)} {CURRENCY}
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<AddShoppingCartIcon />}
                        onClick={() => handleAddToCart(booking)}
                        disabled={cartItemIds.has(booking.id)}
                      >
                        {t('customerDetail.addToCart')}
                      </Button>
                    </Box>
                  ))
                })()}
              </LessonCard>
            ))}
          </Box>
        ))}
      </Box>

      {/* Cart drawer */}
      <CustomerCart
        customerId={customerId!}
        items={cartItems}
        lessons={lessons}
        onRemoveLesson={handleRemoveLessonFromCart}
        onPaid={handlePaid}
      />

      {/* Edit customer dialog */}
      <CustomerDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSaved={handleCustomerSaved}
        customer={customer}
      />
    </>
  )
}
